/** Calendar primitives shared by scheduling, practice and student displays. */
const zoneCache = new Map<string, string | null>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();

/** Inclusive start and exclusive end, expressed as UTC epoch milliseconds. */
export interface DayBounds {
  start: number;
  end: number;
}

/** Validate and canonicalize a named timezone; numeric offsets are not zones. */
export function normalizeTimeZone(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim() || value.length > 100)
    return null;
  const zone = value.trim();
  if (/^[+-]/.test(zone)) return null;
  if (zoneCache.has(zone)) return zoneCache.get(zone)!;
  let resolved: string | null = null;
  try {
    resolved = new Intl.DateTimeFormat('en', {
      timeZone: zone,
    }).resolvedOptions().timeZone;
  } catch {
    /* Invalid user input must not change an existing timezone. */
  }
  // Bound caches reached by user input.
  if (zoneCache.size >= 256) zoneCache.clear();
  zoneCache.set(zone, resolved);
  return resolved;
}

export function dateKeyInTimeZone(date: Date, timeZone: string): string {
  let formatter = dateFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    if (dateFormatters.size >= 256) dateFormatters.clear();
    dateFormatters.set(timeZone, formatter);
  }
  const parts = Object.fromEntries(
    formatter.formatToParts(date).map(part => [part.type, part.value])
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** Calendar-date ordinal, independent of the number of hours in that day. */
export function calendarDayNumber(dateKey: string): number {
  return Math.floor(Date.parse(`${dateKey}T00:00:00Z`) / 86_400_000);
}
